import findDialogTemplate from './findDialog.html';

/*******************************************************************************
 * findDialogDirective */
/**
 * The input fields in this dialog provide data to a filter object. When the
 * search button is clicked, the filter object is sent to the server to retrieve
 * all deposits that match those filters. Upon returning from the server, the
 * deposit list is filled with the matching deposits.
 *
 * Any deposit may be selected, at which point the View button should be enabled.
 * This View button, when activated, should send the Deposit ID to the viewAction
 * provided by the calling component, and close the dialog.
 *
 * If the user needs to clear out the list of deposits and/or the input fields,
 * the Clear button will do both of these.
 *
 * Clicking on the Close button will dismiss the dialog.
 *******************************************************************************/
/* @ngInject */ export function findDialogDirective(ClickDialogDirective) {
  return new ClickDialogDirective({
    template: findDialogTemplate,
    scope: {
      bankAccounts: '&',
      viewAction: '&',
      filters: '<',
      results: '<'
    },
    size: 'find-dialog',
    controller: Controller
  });
}

/*******************************************************************************
 * Controller */
/**
 * Initializes the dialog by setting the bankAccounts list from the directive's
 * expression, as well as creating the uiGrid object. All functions that require
 * injections will be set up here as well, passing in the controller and scope
 * references.
 *******************************************************************************/
/* @ngInject */ function Controller(
  $injector,
  $scope,
  $uibModalInstance,
  uiGridConstants,
  SERVER,
  Tools
) {
  /** Self reference. */ var $ctrl = this;
  $ctrl.bankAccounts = $ctrl.bankAccounts();

  /*------------------------------------------------------*/
  /* The dateRange object must exist before the template  */
  /* links so the date-range-selector and the date-range  */
  /* inputs bind to the same object. Otherwise each       */
  /* creates its own and the selector values are lost.    */
  /*------------------------------------------------------*/
  $ctrl.filters = {
    dateRange: {
      startDate: Tools.lastFiscalYearStart(
        SERVER.BooksOrgInfo.fyMonth,
        SERVER.BooksOrgInfo.fyDay
      ),
      endDate: null
    }
  };

  // Reset the start date filter.
  $ctrl.clearStartDate = function () {
    $ctrl.filters.dateRange.startDate = null;
  };

  /*----------------------------------*/
  /* Add functions to the controller. */
  /*----------------------------------*/
  $ctrl.clear = $injector.invoke(clear, null, {
    $ctrl: $ctrl,
    $scope: $scope
  });
  $ctrl.close = $injector.invoke(close, null, {
    $ctrl: $ctrl,
    $scope: $scope,
    $uibModalInstance: $uibModalInstance
  });
  $ctrl.onBlur = $injector.invoke(onBlur, null, {
    $ctrl: $ctrl,
    $scope: $scope
  });
  $ctrl.search = $injector.invoke(search, null, {
    $ctrl: $ctrl,
    $scope: $scope
  });
  $ctrl.view = $injector.invoke(view, null, {
    $ctrl: $ctrl,
    $scope: $scope,
    $uibModalInstance: $uibModalInstance
  });

  /*---------------------------------*/
  /* Template to add double click    */
  /* functionality to each grid row. */
  /*---------------------------------*/
  var dblClickTemplate =
    '<div ng-dblclick="grid.appScope.$ctrl.view(row.entity)" ng-repeat="(colRenderIndex, col) in colContainer.renderedColumns track by col.colDef.name" class="ui-grid-cell" ui-grid-cell></div>';

  $ctrl.grid = {
    enableColumnMenus: false,
    enableFiltering: false,
    enableFullRowSelection: true,
    enableSelectAll: false,
    enableSelectionBatchEvent: false,
    multiSelect: false,
    rowTemplate: dblClickTemplate,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.NEVER,
    enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableRowSelection: true,
    enableRowHeaderSelection: false,
    onRegisterApi: function (api) {
      $ctrl.grid.api = api;
    },
    data: $ctrl.results,
    columnDefs: [
      {
        field: 'bankAccount'
      },
      {
        field: 'entryDate',
        displayName: 'Date',
        type: 'date',
        cellFilter: "date:'MM/dd/yyyy'"
      },
      {
        field: 'amount',
        type: 'number',
        cellFilter: 'number:2',
        cellClass: 'accounting'
      },
      {
        field: 'status'
      }
    ]
  };
}

/*******************************************************************************
 * clear */
/**
 * @ngdoc     method
 * @name      clear
 * @methodof  csm.books.MakeDeposit.controller:Controller
 * @param  {Boolean}  keepFilters  True=maintain filter object, False=clear filters.
 * description
 * Clears results list, and optionally filters.
 *******************************************************************************/
/* @ngInject */ function clear($ctrl) {
  return function (keepFilters) {
    $ctrl.grid.data.length = 0;

    if (!keepFilters) {
      $ctrl.filters.bankAccountID = NaN;
      $ctrl.filters.dateRange = { startDate: null, endDate: null };
      $ctrl.filters.amountRange = { startAmount: NaN, endAmount: NaN };
      $ctrl.filters.receiptNumber = NaN;
      $ctrl.filters.handwritten = false;
    }
  };
}

/*******************************************************************************
 * close */
/**
 * @ngdoc     method
 * @name      close
 * @methodof  csm.books.MakeDeposit.controller:Controller
 * @description
 * Dismisses the dialog.
 *******************************************************************************/
/* @ngInject */ function close($uibModalInstance) {
  return function () {
    $uibModalInstance.dismiss('cancel');
  };
}

/*******************************************************************************
 * onBlur */
/**
 * @ngdoc    method
 * @name     onBlur
 * @methodof csm.books.MakeDeposit.controller.Controller
 * @param {Object} $event
 * @description
 * Handles the blur events for input fields
 *******************************************************************************/
/* @ngInject */ function onBlur($ctrl) {
  return function () {
    /*------------------------------------------------*/
    /* Keep only the first set of numeric characters. */
    /*------------------------------------------------*/
    $ctrl.filters.receiptNumber =
      $ctrl.filters?.receiptNumber?.replace(/\D.*/, '') || '';

    if ($ctrl.filters.receiptNumber) {
      $ctrl.clearStartDate();
    }
  };
}

/*******************************************************************************
 * search */
/**
 * @ngdoc     method
 * @name      search
 * @methodof  csm.books.MakeDeposit.controller:Controller
 * @description
 * Calls the server to find all deposits matching the filters, and adds them to
 * the grid. If there is only one deposit returned, simulate the view call for
 * the first element of the results.
 *******************************************************************************/
/* @ngInject */ function search($ctrl, Alert, BusyScreen, DepositsService) {
  return function () {
    /*------------------------------------------------------------------*/
    /* Set default values for the filters before sending to the server. */
    /*------------------------------------------------------------------*/
    var filters = {};
    filters.handwritten = $ctrl.filters.handwritten || false;
    filters.bankAccountID = $ctrl.filters.bankAccountID || 0;
    filters.receiptNumber = $ctrl.filters.receiptNumber || 0;
    filters.startAmt = $ctrl.filters.amountRange.startAmount;
    filters.endAmt = $ctrl.filters.amountRange.endAmount;
    filters.startDate = $ctrl.filters.dateRange.startDate;
    filters.endDate = $ctrl.filters.dateRange.endDate;

    /*------------------*/
    /* Call the server. */
    /*------------------*/
    BusyScreen.promise(
      DepositsService.findDeposits(filters).then(function (data) {
        /*---------------------------------------------*/
        /* If there were no results, we can stop here. */
        /*---------------------------------------------*/
        if (data.length == 0) {
          Alert.info('No deposits were found', 'No Deposits Found');
          return;
        }

        data.forEach(function (listItem) {
          /*--------------------------------------------*/
          /* For each deposit item, set the status text */
          /* based on the cleared or printed values.    */
          /*--------------------------------------------*/
          listItem.status = listItem.cleared
            ? 'Cleared'
            : listItem.printed
              ? 'Printed'
              : '';

          /*----------------------------*/
          /* Set the bank account name. */
          /*----------------------------*/
          for (var i = 0; i < $ctrl.bankAccounts.length; i++)
            if ($ctrl.bankAccounts[i].accountID == listItem.bankAccountID) {
              listItem.bankAccount = $ctrl.bankAccounts[i].accountName;
              break;
            }
        });

        /*----------------------------------------------*/
        /* Reset the grid without losing the reference, */
        /* as it is being used in the main application. */
        /*----------------------------------------------*/
        $ctrl.clear(true);
        $ctrl.grid.data.splice.apply($ctrl.grid.data, [0, 0].concat(data));

        /*-----------------------------------------------------------------*/
        /* If there was only one deposit returned, we can assume the user  */
        /* will want to view it, and skip straight to the `view` function. */
        /*-----------------------------------------------------------------*/
        if (data.length == 1) $ctrl.view(data[0]);
      })
    );
  };
}

/*******************************************************************************
 * view */
/**
 * @ngdoc     method
 * @name      view
 * @methodof  csm.books.MakeDeposit.controller:controller
 * @param     {Object}  row  Selected row to view.
 * @description
 * Sends the selected deposit back to the main application for viewing.
 *******************************************************************************/
/* @ngInject */ function view($ctrl, $uibModalInstance, Alert) {
  return function (row) {
    var deposits = row ? [row] : $ctrl.grid.api.selection.getSelectedRows();

    /*--------------------------------------------------------*/
    /* There is nothing to view if there is nothing selected. */
    /*--------------------------------------------------------*/
    if (!deposits.length) {
      Alert.error('A deposit must be selected', 'No Deposit Selected');
      return;
    }

    /*---------------------------------------------------------------*/
    /* Create the output object, and call the `viewAction` function. */
    /*---------------------------------------------------------------*/
    var output = { $depositID: deposits[0].id };
    $ctrl.viewAction(output);
    $uibModalInstance.close();
  };
}
